import { useCallback, useEffect, useState } from 'react';
import { sendVoiceCommand, VoiceBridgeState } from './localVoiceBridge';

type MicrophoneTrack = {
  setDevice: (deviceId: string) => Promise<void>;
  getMediaStreamTrack?: () => MediaStreamTrack;
};
type VoiceWindow = Window & {
  localTracks?: { audioTrack?: MicrophoneTrack | null };
  currentAudioDevice?: string;
};
const activeTrack = () => (window as VoiceWindow).localTracks?.audioTrack;

export function useMicrophoneDevices(open: boolean, voice: VoiceBridgeState) {
  const [browserDevices, setBrowserDevices] = useState<MediaDeviceInfo[]>([]);
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [needsPermission, setNeedsPermission] = useState(false);
  const media = navigator.mediaDevices;

  const refresh = useCallback(async (requestPermission = false) => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    setBusy(true);
    setError('');
    let permissionStream: MediaStream | undefined;
    try {
      if (requestPermission) permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      const devices = (await navigator.mediaDevices.enumerateDevices()).filter((device) => device.kind === 'audioinput');
      setBrowserDevices(devices.filter((device) => device.deviceId));
      setNeedsPermission(devices.length === 0 || devices.some((device) => !device.label));
      setChosen((current) => current.startsWith('browser:') && !devices.some((device) => `browser:${device.deviceId}` === current) ? '' : current);
    } catch (cause) {
      const denied = cause instanceof Error && (cause.name === 'NotAllowedError' || cause.name === 'SecurityError');
      setError(denied ? 'Microphone access is blocked. Allow it in your browser’s site settings, then refresh.' : 'Could not load microphones. Check your device connection and try again.');
      setNeedsPermission(true);
    } finally {
      // Enumeration must not leave a second capture stream running or unmute voice.
      permissionStream?.getTracks().forEach((track) => track.stop());
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void refresh();
    const changed = () => { void refresh(); };
    media?.addEventListener?.('devicechange', changed);
    return () => media?.removeEventListener?.('devicechange', changed);
  }, [open, voice.connected, media, refresh]);

  // Prefer the active web SDK track; retain Unity's device-name bridge for other runtimes.
  const browserMode = typeof activeTrack()?.setDevice === 'function' || voice.inputDevices.length === 0;
  const devices = browserMode
    ? browserDevices.map((device, index) => ({ id: `browser:${device.deviceId}`, label: device.label || `Microphone ${index + 1}`, deviceId: device.deviceId }))
    : voice.inputDevices.map((name) => ({ id: `unity:${name}`, label: name, deviceId: '' }));
  let activeId = '';
  try { activeId = activeTrack()?.getMediaStreamTrack?.().getSettings().deviceId || ''; } catch { /* A track can end while a device is removed. */ }
  const current = devices.find((device) => device.deviceId === activeId && activeId)?.id || devices.find((device) => device.label === voice.inputDevice)?.id || '';
  const selected = devices.some((device) => device.id === chosen) ? chosen : current;
  const canSwitch = voice.connected && voice.enabled && (!browserMode || typeof activeTrack()?.setDevice === 'function');

  const select = async (id: string) => {
    const device = devices.find((item) => item.id === id);
    if (!device || !canSwitch) return;
    setBusy(true);
    setError('');
    try {
      if (browserMode) {
        const track = activeTrack();
        if (!track) throw new Error('Voice microphone is not ready.');
        await track.setDevice(device.deviceId);
        (window as VoiceWindow).currentAudioDevice = device.deviceId;
      } else if (!sendVoiceCommand('setInputDevice', { deviceName: device.label })) {
        throw new Error('Voice controls are not ready.');
      }
      setChosen(id);
    } catch {
      setError('Could not switch microphones. Check your device connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  return { devices, selected, busy, error, needsPermission, canSwitch, supported: !!media?.enumerateDevices, refresh, select };
}
